import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Snackbar, Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { gamesApi, getSocket } from '../../services/api/gamesApi';
import { authApi } from '../../services/api/authApi';
import { unlockLudoAudio } from './ludoSounds.js';
import { LUDO_STAKE_PRESETS } from './ludoConstants.js';
import { useLudoPresence } from './useLudoPresence.js';
import LudoOnlinePanel from './LudoOnlinePanel.jsx';
import './LudoLobbyScreen.css';

function LudoLobbyScreen({ user, onBack, onStartGame, onWalletUpdate }) {
  const [state, setState] = useState(null);
  const [selectedStake, setSelectedStake] = useState(100);
  const [myAvailability, setMyAvailability] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);

  const refreshUserWallet = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch { /* ignore */ }
  }, [onWalletUpdate]);

  const refresh = useCallback(async () => {
    try {
      const s = await gamesApi.ludoState();
      setState(s);
      if (s.activeRoom?.status === 'PLAYING') {
        onStartGame(s.activeRoom.id);
      } else if (s.activeRoom?.status === 'WAITING') {
        setState(s);
      }
    } catch (err) {
      setToast(err.message || 'Could not load Ludo lobby');
    }
  }, [onStartGame]);

  const {
    online,
    pendingInvites,
    loading: presenceLoading,
    refreshing: presenceRefreshing,
    refresh: refreshPresence,
    sendInvite: sendInvitePresence,
    respondInvite: respondInvitePresence,
  } = useLudoPresence({
    enabled: true,
    joinLobby: true,
    autoResume: true,
    availableStake: myAvailability,
    onMatchFound: onStartGame,
    onInviteReceived: () => refresh(),
  });

  const isBusy = loading || presenceLoading;

  useEffect(() => {
    refreshUserWallet();
  }, [refreshUserWallet]);

  useEffect(() => {
    unlockLudoAudio();
  }, []);

  useEffect(() => {
    if (user?.walletBalance != null) {
      setWalletBalance(user.walletBalance);
    }
  }, [user?.walletBalance]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runWithRules = (action) => {
    const seen = sessionStorage.getItem('ludo_rules_accepted');
    if (!seen) {
      setPendingAction(() => action);
      setRulesOpen(true);
      return;
    }
    action();
  };

  const acceptRules = () => {
    sessionStorage.setItem('ludo_rules_accepted', '1');
    setRulesOpen(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const setAvailability = async (stake) => {
    setLoading(true);
    try {
      await gamesApi.ludoSetAvailability(stake);
      setMyAvailability(stake);
      getSocket().emit('join:ludo-lobby', { availableStake: stake });
      setToast(stake ? `You are available at ₹${stake}` : 'Availability cleared');
    } catch (err) {
      setToast(err.message || 'Could not update availability');
    } finally {
      setLoading(false);
    }
  };

  const startBotGame = async () => {
    setLoading(true);
    try {
      const result = await gamesApi.ludoCreateBotGame();
      setToast('Starting free practice vs Bot');
      onStartGame(result.room.id);
    } catch (err) {
      setToast(err.message || 'Could not start bot game');
    } finally {
      setLoading(false);
    }
  };

  const createTable = async () => {
    if (selectedStake > walletBalance) {
      setToast('Insufficient wallet balance');
      return;
    }
    setLoading(true);
    try {
      const result = await gamesApi.ludoCreateRoom(selectedStake);
      await refreshUserWallet();
      setToast(`Table created · stake ₹${selectedStake}`);
      if (result.room) setState((prev) => ({ ...prev, activeRoom: result.room }));
    } catch (err) {
      setToast(err.message || 'Could not create table');
    } finally {
      setLoading(false);
    }
  };

  const joinTable = async (roomId) => {
    setLoading(true);
    try {
      const result = await gamesApi.ludoJoin({ roomId });
      await refreshUserWallet();
      onStartGame(result.room.id);
    } catch (err) {
      setToast(err.message || 'Could not join table');
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async (toUserId, stake) => {
    setLoading(true);
    try {
      const ok = await sendInvitePresence(toUserId, stake);
      if (ok) setToast('Invite sent');
      else setToast('Could not send invite');
      refresh();
    } finally {
      setLoading(false);
    }
  };

  const respondInvite = async (inviteId, accept) => {
    setLoading(true);
    try {
      await respondInvitePresence(inviteId, accept);
      await refreshUserWallet();
      refresh();
    } catch (err) {
      setToast(err.message || 'Could not respond to invite');
    } finally {
      setLoading(false);
    }
  };

  const cancelWaitingRoom = async () => {
    if (!state?.activeRoom?.id) return;
    setLoading(true);
    try {
      await gamesApi.ludoLeave(state.activeRoom.id);
      await refreshUserWallet();
      refresh();
      setToast('Table cancelled · stake refunded');
    } catch (err) {
      setToast(err.message || 'Could not cancel table');
    } finally {
      setLoading(false);
    }
  };

  const feePercent = state?.platformCommissionPercent ?? 7;
  const winnerGets = Math.round(selectedStake * 2 * (1 - feePercent / 100));

  const handleBack = async () => {
    if (state?.activeRoom?.status === 'WAITING') {
      const ok = window.confirm('Cancel your open table? Your stake will be refunded.');
      if (!ok) return;
      setLoading(true);
      try {
        await gamesApi.ludoLeave(state.activeRoom.id);
        await refreshUserWallet();
      } catch (err) {
        setToast(err.message || 'Could not cancel table');
        return;
      } finally {
        setLoading(false);
      }
    }
    onBack();
  };

  return (
    <Box className="ludo-lobby">
      <header className="ludo-lobby__header">
        <IconButton size="small" onClick={handleBack} sx={{ color: '#fff' }}>
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography className="ludo-lobby__title">Ludo</Typography>
          <Typography className="ludo-lobby__subtitle">2-player · real stakes</Typography>
        </Box>
        <Box className="ludo-lobby__wallet">
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16 }} />
          <span>₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </Box>
      </header>

      <Box className="ludo-lobby__scroll">
        <Box className="ludo-lobby__rules-card">
          <Typography component="p">
            <strong>Before you play:</strong> {feePercent}% platform fee on the total pool.
            Winner takes the rest. If you leave mid-game, you forfeit and lose your stake.
          </Typography>
        </Box>

        <Typography className="ludo-lobby__section">Choose stake (₹)</Typography>
        <Box className="ludo-lobby__stakes">
          {LUDO_STAKE_PRESETS.map((stake) => (
            <button
              key={stake}
              type="button"
              className={`ludo-stake-chip ${selectedStake === stake ? 'ludo-stake-chip--active' : ''}`}
              onClick={() => setSelectedStake(stake)}
            >
              ₹{stake}
            </button>
          ))}
        </Box>
        <Typography className="ludo-lobby__pool-note">
          Winner gets ₹{winnerGets.toLocaleString('en-IN')} from ₹{(selectedStake * 2).toLocaleString('en-IN')} pool
        </Typography>

        <Box className="ludo-lobby__actions">
          <Button
            variant="contained"
            fullWidth
            disabled={isBusy}
            onClick={() => runWithRules(() => setAvailability(selectedStake))}
          >
            Show available · ₹{selectedStake}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            disabled={isBusy}
            onClick={() => runWithRules(createTable)}
          >
            Create open table · ₹{selectedStake}
          </Button>
        </Box>

        <Typography className="ludo-lobby__section">Practice</Typography>
        <Button
          variant="contained"
          fullWidth
          disabled={isBusy}
          className="ludo-lobby__bot-btn"
          onClick={startBotGame}
        >
          Play vs Bot · Free
        </Button>
        <Typography className="ludo-lobby__pool-note">
          No wallet charge — same board and rules as PvP.
        </Typography>

        {state?.activeRoom?.status === 'WAITING' && (
          <Box className="ludo-lobby__waiting">
            <Typography>Your table is open · ₹{state.activeRoom.stake}</Typography>
            <Typography className="ludo-lobby__waiting-code">
              Code: {state.activeRoom.roomCode}
            </Typography>
            <Button size="small" color="error" onClick={cancelWaitingRoom} disabled={loading}>
              Cancel & refund
            </Button>
          </Box>
        )}

        <LudoOnlinePanel
          online={online}
          pendingInvites={pendingInvites}
          inviteStake={selectedStake}
          onRefresh={refreshPresence}
          refreshing={presenceRefreshing}
          onSendInvite={(id, stake) => runWithRules(() => sendInvite(id, stake))}
          onRespondInvite={(id, accept) => (accept ? runWithRules(() => respondInvite(id, true)) : respondInvite(id, false))}
          loading={isBusy}
        />

        <Typography className="ludo-lobby__section">Open tables</Typography>
        {(state?.openRooms?.length ?? 0) === 0 ? (
          <Typography className="ludo-lobby__empty">No open tables — create one above</Typography>
        ) : (
          state.openRooms.map((room) => (
            <Box key={room.id} className="ludo-lobby__player-row">
              <Box>
                <Typography className="ludo-lobby__player-name">{room.host.name}</Typography>
                <Typography className="ludo-lobby__player-meta">
                  Stake ₹{room.stake} · {room.roomCode}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={loading || room.stake > walletBalance}
                onClick={() => runWithRules(() => joinTable(room.id))}
              >
                Join ₹{room.stake}
              </Button>
            </Box>
          ))
        )}
      </Box>

      <Dialog open={rulesOpen} onClose={() => setRulesOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ludo rules</DialogTitle>
        <DialogContent>
          <Typography component="p" sx={{ mb: 1.5 }}>
            • 2 players only — each puts the same stake before the match starts.
          </Typography>
          <Typography component="p" sx={{ mb: 1.5 }}>
            • {feePercent}% platform fee is deducted from the total pool (both stakes combined).
          </Typography>
          <Typography component="p" sx={{ mb: 1.5 }}>
            • Winner receives the remaining amount in their wallet.
          </Typography>
          <Typography component="p" color="error.main">
            • Leaving or quitting mid-game counts as a forfeit — you lose and your opponent wins the pool.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRulesOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={acceptRules}>I understand · continue</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} message={toast} autoHideDuration={3500} onClose={() => setToast('')} />
    </Box>
  );
}

export default LudoLobbyScreen;
